import {
  forwardRef,
  useState,
  useRef,
  useEffect,
  useCallback,
  cloneElement,
  isValidElement,
} from 'react';
import styled from '@emotion/styled';
import type {
  DropdownMenuProps,
  DropdownMenuPlacement,
  DropdownMenuType,
  DropdownMenuFontSize,
  DropdownMenuItem,
} from './DropdownMenu.types';
import { isDivider, isHeader, isMenuItem } from './DropdownMenu.types';

/**
 * Container for dropdown menu
 */
const Container = styled.div`
  position: relative;
  display: inline-block;
`;

/**
 * Trigger wrapper for consistent styling
 */
const TriggerWrapper = styled.div`
  display: inline-flex;
`;

/**
 * Default trigger button (vertical dots icon)
 */
const DefaultTriggerButton = styled.button<{
  $inverse: boolean;
  $disabled: boolean;
  $size: 'xs' | 'sm' | 'md';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  padding: ${({ $size }) => {
    switch ($size) {
      case 'xs': return '4px';
      case 'sm': return '6px';
      case 'md': default: return '8px';
    }
  }};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  color: ${({ $inverse, theme }) => ($inverse ? theme.colors.white : theme.colors.text.secondary)};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.background.hover};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlight};
    outline-offset: 2px;
  }
`;

/**
 * Three dots icon for default trigger
 */
const MoreVertIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

/**
 * Get placement styles for the menu panel
 */
const getPlacementStyles = (placement: DropdownMenuPlacement) => {
  switch (placement) {
    case 'bottom-end':
      return `
        top: 100%;
        right: 0;
        margin-top: 4px;
      `;
    case 'top-start':
      return `
        bottom: 100%;
        left: 0;
        margin-bottom: 4px;
      `;
    case 'top-end':
      return `
        bottom: 100%;
        right: 0;
        margin-bottom: 4px;
      `;
    case 'bottom-start':
    default:
      return `
        top: 100%;
        left: 0;
        margin-top: 4px;
      `;
  }
};

/**
 * Menu panel that appears on trigger click
 */
const MenuPanel = styled.div<{
  $placement: DropdownMenuPlacement;
  $isOpen: boolean;
  $width?: string;
}>`
  position: absolute;
  z-index: 1500;
  ${({ $placement }) => getPlacementStyles($placement)}
  
  min-width: 136px;
  ${({ $width }) => $width && `width: ${$width};`}
  
  background-color: ${({ theme }) => theme.colors.background.default};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  box-shadow: ${({ theme }) => theme.shadows.menu};
  
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(-4px)')};
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;
  
  overflow: hidden;
`;

/**
 * Menu title header
 */
const MenuTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.grey[100]};
  height: 32px;
  padding-left: 12px;
  border-top-left-radius: ${({ theme }) => theme.borders.radius.sm};
  border-top-right-radius: ${({ theme }) => theme.borders.radius.sm};
`;

/**
 * Section header wrapper
 */
const SectionHeaderWrapper = styled.div<{ $first: boolean }>`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.grey[100]};
  padding: 8px 16px;
  ${({ $first }) => $first && 'margin-top: -4px;'}
`;

/**
 * Header/title text
 */
const HeaderText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

/**
 * Menu list container
 */
const MenuList = styled.ul<{ $width?: string; $height?: string }>`
  list-style: none;
  margin: 0;
  padding: 4px 0;
  ${({ $width }) => $width && `width: ${$width};`}
  ${({ $height }) => $height && `max-height: ${$height}; overflow-y: auto;`}
`;

/**
 * Get font size value based on size option
 */
const getFontSize = (size: DropdownMenuFontSize, theme: any) => {
  switch (size) {
    case 'xs': return theme.typography.fontSize.xs;
    case 'sm': return theme.typography.fontSize.sm;
    case 'md': return theme.typography.fontSize.md;
    default: return theme.typography.fontSize.sm;
  }
};

/**
 * Individual menu item
 */
const MenuItemButton = styled.li<{
  $type: DropdownMenuType;
  $disabled: boolean;
  $selected: boolean;
  $isInvalid: boolean;
  $isBold: boolean;
  $isFocused: boolean;
  $itemHeight?: string;
  $fontSize: DropdownMenuFontSize;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  height: ${({ $itemHeight }) => $itemHeight || '36px'};
  padding: 0 12px;
  font-size: ${({ $fontSize, theme }) => getFontSize($fontSize, theme)};
  font-weight: ${({ $isBold, theme }) => 
    $isBold ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.regular};
  color: ${({ $disabled, $isInvalid, theme }) =>
    $disabled || $isInvalid ? theme.colors.text.muted : theme.colors.text.primary};
  cursor: ${({ $disabled, $isInvalid }) =>
    $disabled || $isInvalid ? 'not-allowed' : 'pointer'};
  background-color: ${({ $selected, theme }) =>
    $selected ? `${theme.colors.highlight}30` : 'transparent'};
  transition: ${({ theme }) => theme.transitions.fast};
  
  /* Focused state (keyboard navigation) */
  ${({ $isFocused, theme }) =>
    $isFocused &&
    `
    outline: 2px solid ${theme.colors.highlight};
    outline-offset: -2px;
  `}
  
  /* Hover state based on type */
  &:hover {
    ${({ $disabled, $isInvalid, $type, theme }) => {
      if ($disabled || $isInvalid) {
        return 'background-color: transparent;';
      }
      
      switch ($type) {
        case 'selection':
          return `
            background-color: ${theme.colors.highlight};
            color: ${theme.colors.highlightText};
            
            /* Also change icon color on hover */
            span {
              color: ${theme.colors.highlightText};
            }
          `;
        case 'action':
          return `
            background-color: ${theme.colors.background.hover};
          `;
        case 'display':
        default:
          return '';
      }
    }}
  }
`;

/**
 * Icon wrapper for menu items
 */
const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-right: 8px;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

/**
 * Divider between sections
 */
const Divider = styled.li`
  height: 1px;
  margin: 4px 0;
  background-color: ${({ theme }) => theme.colors.border.light};
`;

/**
 * DropdownMenu component - A flexible dropdown menu with keyboard navigation
 *
 * @example
 * ```tsx
 * // Basic usage with custom trigger
 * <DropdownMenu
 *   trigger={<Button>Actions</Button>}
 *   items={[
 *     { id: 'edit', displayText: 'Edit', onClick: handleEdit },
 *     { id: 'delete', displayText: 'Delete', onClick: handleDelete },
 *   ]}
 * />
 *
 * // With icons and dividers
 * <DropdownMenu
 *   items={[
 *     { id: 'copy', displayText: 'Copy', icon: <CopyIcon /> },
 *     { type: 'divider' },
 *     { id: 'delete', displayText: 'Delete', disabled: true },
 *   ]}
 *   placement="bottom-end"
 * />
 * ```
 */
export const DropdownMenu = forwardRef<HTMLDivElement, DropdownMenuProps>(
  (
    {
      items,
      trigger,
      open: controlledOpen,
      onOpenChange,
      placement = 'bottom-end',
      disabled = false,
      type = 'action',
      title,
      buttonSize = 'md',
      inverse = false,
      menuListWidth,
      menuListHeight,
      menuItemHeight,
      useContainerWidth = false,
      buttonTabIndex,
      onOpen,
      onClose,
      className,
      dataTestId,
      dataId,
      fontSize = 'sm',
      'aria-label': ariaLabel = 'Menu',
    },
    ref
  ) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [isKeyboardNav, setIsKeyboardNav] = useState(false);
    
    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : internalOpen;
    
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const itemRefs = useRef<Map<number, HTMLLIElement>>(new Map());
    
    // Get only interactive menu items (not headers or dividers)
    const menuItems = items.filter(isMenuItem) as DropdownMenuItem[];
    const enabledItems = menuItems.filter(item => !item.disabled && !item.isInvalid);
    
    const setIsOpen = useCallback(
      (open: boolean) => {
        if (!isControlled) {
          setInternalOpen(open);
        }
        onOpenChange?.(open);
        
        if (open) {
          onOpen?.();
        } else {
          onClose?.();
          setFocusedIndex(-1);
          setIsKeyboardNav(false);
        }
      },
      [isControlled, onOpenChange, onOpen, onClose]
    );
    
    const handleTriggerClick = useCallback(
      (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (!disabled) {
          setIsOpen(!isOpen);
        }
      },
      [disabled, isOpen, setIsOpen]
    );
    
    const handleTriggerKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (!disabled) {
            setIsKeyboardNav(true);
            setIsOpen(!isOpen);
          }
        } else if (event.key === 'Escape' && isOpen) {
          event.preventDefault();
          setIsOpen(false);
          triggerRef.current?.focus();
        }
      },
      [disabled, isOpen, setIsOpen]
    );
    
    const handleItemClick = useCallback(
      (item: DropdownMenuItem) => {
        if (item.disabled || item.isInvalid) return;
        
        item.onClick?.(item.value);
        setIsOpen(false);
        setTimeout(() => triggerRef.current?.focus(), 0);
      },
      [setIsOpen]
    );
    
    const handleItemKeyDown = useCallback(
      (event: React.KeyboardEvent, item: DropdownMenuItem) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          if (!item.disabled && !item.isInvalid) {
            handleItemClick(item);
          }
        }
      },
      [handleItemClick]
    );
    
    // Click outside to close
    useEffect(() => {
      if (!isOpen) return;
      
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, setIsOpen]);
    
    // Keyboard navigation for menu
    useEffect(() => {
      if (!isOpen) return;
      
      const handleKeyDown = (event: KeyboardEvent) => {
        switch (event.key) {
          case 'Escape':
            event.preventDefault();
            setIsOpen(false);
            triggerRef.current?.focus();
            break;
            
          case 'ArrowDown':
            event.preventDefault();
            setIsKeyboardNav(true);
            setFocusedIndex(prev => {
              const enabledIndices = menuItems
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => !item.disabled && !item.isInvalid)
                .map(({ idx }) => idx);
              
              if (enabledIndices.length === 0) return -1;
              
              const currentPos = enabledIndices.indexOf(prev);
              const nextPos = currentPos === -1 || currentPos === enabledIndices.length - 1
                ? 0
                : currentPos + 1;
              
              return enabledIndices[nextPos] ?? -1;
            });
            break;
            
          case 'ArrowUp':
            event.preventDefault();
            setIsKeyboardNav(true);
            setFocusedIndex(prev => {
              const enabledIndices = menuItems
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => !item.disabled && !item.isInvalid)
                .map(({ idx }) => idx);
              
              if (enabledIndices.length === 0) return -1;
              
              const currentPos = enabledIndices.indexOf(prev);
              const nextPos = currentPos === -1 || currentPos === 0
                ? enabledIndices.length - 1
                : currentPos - 1;
              
              return enabledIndices[nextPos] ?? -1;
            });
            break;
            
          case 'Home':
            event.preventDefault();
            setIsKeyboardNav(true);
            {
              const firstEnabled = menuItems.findIndex(item => !item.disabled && !item.isInvalid);
              if (firstEnabled !== -1) setFocusedIndex(firstEnabled);
            }
            break;
            
          case 'End':
            event.preventDefault();
            setIsKeyboardNav(true);
            {
              const lastEnabled = [...menuItems].reverse().findIndex(item => !item.disabled && !item.isInvalid);
              if (lastEnabled !== -1) setFocusedIndex(menuItems.length - 1 - lastEnabled);
            }
            break;
        }
      };
      
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, menuItems, setIsOpen]);
    
    // Focus menu item when index changes via keyboard
    useEffect(() => {
      if (isOpen && isKeyboardNav && focusedIndex >= 0) {
        const element = itemRefs.current.get(focusedIndex);
        element?.focus();
      }
    }, [isOpen, isKeyboardNav, focusedIndex]);
    
    // Focus first item when opened via keyboard
    useEffect(() => {
      if (isOpen && isKeyboardNav && focusedIndex === -1 && enabledItems.length > 0) {
        const firstEnabledIndex = menuItems.findIndex(item => !item.disabled && !item.isInvalid);
        if (firstEnabledIndex !== -1) {
          setFocusedIndex(firstEnabledIndex);
        }
      }
    }, [isOpen, isKeyboardNav, focusedIndex, menuItems, enabledItems.length]);
    
    const setItemRef = useCallback(
      (index: number, element: HTMLLIElement | null) => {
        if (element) {
          itemRefs.current.set(index, element);
        } else {
          itemRefs.current.delete(index);
        }
      },
      []
    );
    
    // Build trigger element
    const triggerElement = trigger ? (
      isValidElement(trigger) ? (
        cloneElement(trigger as React.ReactElement<any>, {
          onClick: (e: React.MouseEvent) => {
            (trigger as React.ReactElement<any>).props?.onClick?.(e);
            handleTriggerClick(e);
          },
          onKeyDown: (e: React.KeyboardEvent) => {
            (trigger as React.ReactElement<any>).props?.onKeyDown?.(e);
            handleTriggerKeyDown(e);
          },
          ref: triggerRef,
          'aria-haspopup': 'menu',
          'aria-expanded': isOpen,
          disabled: disabled,
          tabIndex: buttonTabIndex,
        })
      ) : trigger
    ) : (
      <DefaultTriggerButton
        ref={triggerRef}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        $inverse={inverse}
        $disabled={disabled}
        $size={buttonSize}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Open menu"
        tabIndex={buttonTabIndex}
      >
        <MoreVertIcon />
      </DefaultTriggerButton>
    );
    
    // Track menu item index
    let menuItemIndex = 0;
    
    return (
      <Container
        ref={(node) => {
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        className={className}
        data-testid={dataTestId}
        data-id={dataId}
      >
        <TriggerWrapper>{triggerElement}</TriggerWrapper>
        
        <MenuPanel
          role="menu"
          aria-label={ariaLabel}
          $placement={placement}
          $isOpen={isOpen}
          $width={useContainerWidth && triggerRef.current ? `${triggerRef.current.clientWidth}px` : undefined}
        >
          {title && (
            <MenuTitleWrapper>
              <HeaderText>{title}</HeaderText>
            </MenuTitleWrapper>
          )}
          
          <MenuList
            ref={menuRef}
            $width={menuListWidth}
            $height={menuListHeight}
          >
            {items.map((item, index) => {
              // Divider
              if (isDivider(item)) {
                return (
                  <Divider
                    key={`divider-${index}`}
                    role="separator"
                    aria-orientation="horizontal"
                  />
                );
              }
              
              // Header
              if (isHeader(item)) {
                return (
                  <SectionHeaderWrapper
                    key={`header-${item.id}`}
                    $first={index === 0}
                  >
                    <HeaderText>{item.displayText}</HeaderText>
                  </SectionHeaderWrapper>
                );
              }
              
              // Regular menu item
              const currentIndex = menuItemIndex++;
              const isFocused = currentIndex === focusedIndex && isKeyboardNav;
              
              return (
                <MenuItemButton
                  key={item.id}
                  ref={(el) => setItemRef(currentIndex, el)}
                  role="menuitem"
                  tabIndex={isFocused ? 0 : -1}
                  aria-disabled={item.disabled || item.isInvalid}
                  onClick={() => handleItemClick(item)}
                  onKeyDown={(e) => handleItemKeyDown(e, item)}
                  onMouseEnter={() => {
                    setIsKeyboardNav(false);
                    setFocusedIndex(currentIndex);
                  }}
                  $type={type}
                  $disabled={item.disabled ?? false}
                  $selected={item.selected ?? false}
                  $isInvalid={item.isInvalid ?? false}
                  $isBold={item.isBold ?? false}
                  $isFocused={isFocused}
                  $itemHeight={menuItemHeight}
                  $fontSize={fontSize}
                >
                  {item.icon && <IconWrapper>{item.icon}</IconWrapper>}
                  {item.displayText}
                </MenuItemButton>
              );
            })}
          </MenuList>
        </MenuPanel>
      </Container>
    );
  }
);

DropdownMenu.displayName = 'DropdownMenu';

export default DropdownMenu;
